import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import moment from "moment";
import "moment/locale/gu";
import {
  Plus,
  Search,
  Pencil,
  Trash2,
  Filter,
  ListFilter,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchUsersListData,
  getUsers,
  getTotalUsers,
  getFilter,
  setFilter,
  getUsersLoading,
} from "../../features/users/usersSlice";
import { useCanAccess } from "../../hooks/useCanAccess";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE } from "../../services/axios.service";
import { toastStyles, filterSelectStyles } from "../../static/styles";
import { t } from "../../locales";
import { route } from "../../static/route";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Badge } from "../ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { UserListItem, UsersFilterType } from "../../types/users";

const sortByOptions = [
  { value: "id", label: t.users.sortOptions.id },
  { value: "name", label: t.users.sortOptions.name },
  { value: "email", label: t.users.sortOptions.email },
  { value: "created_at", label: t.users.sortOptions.created_at },
];

const sortOrderOptions = [
  { value: "asc", label: t.users.sortOrderOptions.asc },
  { value: "desc", label: t.users.sortOrderOptions.desc },
];

const formatPhone = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  }
  return phone;
};

const UserList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const canAccess = useCanAccess();
  const confirmDelete = useConfirmPopup();

  moment.locale("gu");

  const users = useAppSelector(getUsers);
  const totalUsers = useAppSelector(getTotalUsers);
  const filter = useAppSelector(getFilter);
  const loading = useAppSelector(getUsersLoading);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState<number>(0);

  const { register, control, handleSubmit, reset } =
    useForm<UsersFilterType>({
      defaultValues: { ...filter },
    });

  const fetchUsers = () => {
    dispatch(
      fetchUsersListData({
        page,
        size: pageSize,
        search: filter.search,
        sortBy: filter.sortBy,
        sortOrder: filter.sortOrder,
      })
    );
  };

  useEffect(() => {
    fetchUsers();
  }, [page, pageSize, filter]);

  const onApplyFilters = (data: UsersFilterType) => {
    dispatch(setFilter({ search: data.search || null, sortBy: data.sortBy, sortOrder: data.sortOrder }));
    setPage(1);
  };

  const handleClearFilters = () => {
    const defaults = { search: null as string | null, sortBy: "id", sortOrder: "desc" };
    reset(defaults);
    dispatch(setFilter(defaults));
    setPage(1);
  };

  const handleDeleteUser = async (id: number) => {
    const confirmed = await confirmDelete(
      t.users.deleteConfirmText,
      t.users.deleteConfirmTitle,
      t.users.deleteConfirmButton
    );

    if (confirmed) {
      setActionLoadingId(id);
      try {
        const response: any = await DELETE(`/v1/users/${id}`);
        if (response.data.status === "success") {
          toast.success(response.data.message, { ...(toastStyles as any) });
          const newPage = users.length > 1 ? page : Math.max(page - 1, 1);
          if (newPage === page) {
            fetchUsers();
          } else {
            setPage(newPage);
          }
        } else {
          toast.error(response.data.message, { ...(toastStyles as any) });
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.message || t.common.somethingWentWrong,
          { ...(toastStyles as any) }
        );
      }
      setActionLoadingId(0);
    }
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);

  const totalPages = Math.ceil(totalUsers / pageSize);

  const getVisiblePages = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("...");
      for (
        let i = Math.max(2, page - 1);
        i <= Math.min(totalPages - 1, page + 1);
        i++
      ) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push("...");
      pages.push(totalPages);
    }
    return pages;
  };

  const start = totalUsers === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalUsers);

  return (
    <div className="space-y-4">
      {/* Top bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-3 ml-auto">
          <Button
            variant="outline"
            className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter className="h-4 w-4 mr-2" />
            {t.users.filterToggle}
          </Button>

          {canAccess("users.create") && (
            <Button
              className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              onClick={() => navigate(route.usersCreate)}
            >
              <Plus className="h-4 w-4 mr-2" />
              {t.users.addNewUser}
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t.users.filterTitle}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-4">
            <form onSubmit={handleSubmit(onApplyFilters)}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.users.searchLabel}
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <Input
                      className="pl-12 h-[48px] text-base border-2 border-gray-200 rounded-lg focus:border-[#113F67] focus:shadow-focus-brand"
                      placeholder={t.users.searchPlaceholder}
                      {...register("search")}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.users.sortByLabel}
                  </label>
                  <div className="relative">
                    <ListFilter className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortBy"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortByOptions}
                          placeholder={t.users.sortByPlaceholder}
                          value={sortByOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "id")
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-semibold text-[#113F67] mb-1 block">
                    {t.users.sortOrderLabel}
                  </label>
                  <div className="relative">
                    <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                    <Controller
                      name="sortOrder"
                      control={control}
                      render={({ field }) => (
                        <ReactSelect
                          styles={filterSelectStyles}
                          options={sortOrderOptions}
                          placeholder={t.users.sortOrderPlaceholder}
                          value={sortOrderOptions.find(
                            (o) => o.value === field.value
                          )}
                          onChange={(opt: any) =>
                            field.onChange(opt?.value || "desc")
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-4 border-t pt-4 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-10 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
                  onClick={handleClearFilters}
                >
                  {t.users.clearFilters}
                </Button>
                <Button
                  type="submit"
                  className="h-10 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
                >
                  {t.users.applyFilters}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading && users.length === 0 ? (
          <Card>
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                {t.users.loading}
              </div>
            </CardContent>
          </Card>
        ) : !loading && users.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-gray-500">
              {t.users.noRecords}
            </CardContent>
          </Card>
        ) : (
          <div className={`space-y-3${loading ? " opacity-50 pointer-events-none" : ""}`}>
          {users.map((user: UserListItem, index: number) => (
            <Card key={user.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="text-xs">
                        {getInitials(user.name || "U")}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span className="font-medium text-gray-900">
                        {user.name || "-"}
                      </span>
                      <div className="text-sm text-gray-500">#{(page - 1) * pageSize + index + 1} · ID: {user.id}</div>
                    </div>
                  </div>
                  <div>
                    {actionLoadingId === user.id ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67]" />
                    ) : (
                      <div className="flex items-center gap-1">
                        {canAccess("users.update") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() =>
                              navigate(`/users/${user.id}/edit`)
                            }
                          >
                            <Pencil className="h-4 w-4 text-[#113F67]" />
                          </Button>
                        )}
                        {canAccess("users.delete") && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleDeleteUser(user.id)}
                          >
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.users.table.phone}</span>
                    <span className="text-gray-700">
                      {user.phone ? formatPhone(user.phone) : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.users.table.email}</span>
                    <span className="text-gray-700 truncate ml-4">
                      {user.email || "-"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">{t.users.table.role}</span>
                    {user.role ? (
                      <Badge variant="secondary">{user.role}</Badge>
                    ) : (
                      <span className="text-gray-700">-</span>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">{t.users.table.permissions}</span>
                    <Badge variant="secondary">{user.permissions_count}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t.users.table.created}</span>
                    <span className="text-gray-700">
                      {moment(user.created_at).format("DD MMMM, YYYY hh:mm A")}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        )}
      </div>

      {/* Table */}
      <Card className="hidden md:block">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">{t.users.table.srNo}</TableHead>
                  <TableHead>{t.users.table.user}</TableHead>
                  <TableHead>{t.users.table.phone}</TableHead>
                  <TableHead>{t.users.table.email}</TableHead>
                  <TableHead>{t.users.table.role}</TableHead>
                  <TableHead>{t.users.table.permissions}</TableHead>
                  <TableHead>{t.users.table.created}</TableHead>
                  <TableHead className="text-right">
                    {t.users.table.actions}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2 text-gray-500">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#113F67]" />
                        {t.users.loading}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      className="text-center py-8 text-gray-500"
                    >
                      {t.users.noRecords}
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user: UserListItem, index: number) => (
                    <TableRow className="align-top" key={user.id}>
                      <TableCell className="font-medium text-gray-500">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-10 w-10">
                            <AvatarFallback className="text-xs">
                              {getInitials(user.name || "U")}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <span className="font-medium text-gray-900">
                              {user.name || "-"}
                            </span>
                            <div className="text-sm text-gray-500">
                              ID: {user.id}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {user.phone ? formatPhone(user.phone) : "-"}
                      </TableCell>
                      <TableCell className="text-gray-600">
                        {user.email || "-"}
                      </TableCell>
                      <TableCell>
                        {user.role ? (
                          <Badge variant="secondary">{user.role}</Badge>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">
                          {user.permissions_count}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">
                        {moment(user.created_at).format(
                          "DD MMMM, YYYY hh:mm A"
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {actionLoadingId === user.id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#113F67] ml-auto" />
                        ) : (
                          <div className="flex items-center justify-end gap-1">
                            {canAccess("users.update") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() =>
                                  navigate(`/users/${user.id}/edit`)
                                }
                              >
                                <Pencil className="h-4 w-4 text-[#113F67]" />
                              </Button>
                            )}
                            {canAccess("users.delete") && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && users.length > 0 && (
        <div className="flex flex-wrap items-center justify-end md:justify-between gap-4">
          <div className="hidden md:flex items-center gap-2 text-sm text-gray-600">
            <span>
              {start}-{end} of {totalUsers} {t.users.pageOfLabel}
            </span>
            <span>{t.users.pageSizeLabel}</span>
            <Select
              value={String(pageSize)}
              onValueChange={(val) => {
                setPageSize(Number(val));
                setPage(1);
              }}
            >
              <SelectTrigger className="w-20 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            {getVisiblePages().map((p, i) =>
              typeof p === "string" ? (
                <span key={`ellipsis-${i}`} className="px-2 text-gray-400">
                  ...
                </span>
              ) : (
                <Button
                  key={p}
                  variant={page === p ? "default" : "outline"}
                  className={`w-8 h-8 p-0 ${
                    page === p ? "bg-[#113F67] hover:bg-[#0d2f4d]" : ""
                  }`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="h-8 w-8 p-0"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
